import listenerMiddleware from "App/listenerMiddleware";
import documentsActionTypes from "Areas/Documents/redux/actionTypes";
import getId from "Areas/SharedFeatures/types/utils/getId";
import isDocumentMapDataGeometry from "Areas/SharedFeatures/types/utils/isDocumentMapDataGeometry";
import isDocumentMapDataPoint from "Areas/SharedFeatures/types/utils/isDocumentMapDataPoint";
import documentsSidebarMenuActionTypes from "Areas/Sidebar/Documents/redux/actionTypes";
import { getSelectedDocumentIds } from "Areas/Sidebar/Documents/redux/getActiveDocumentId";
import createReducer from "Common/reducerUtils/createReducer";
import set from "Common/reducerUtils/set";
import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import forEach from "lodash/forEach";
import get from "lodash/get";
import includes from "lodash/includes";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import omit from "lodash/omit";
import { NAMESPACE } from "../constants";
import { buildGeometryToGeoFeatureClusteredName, buildGeometryToGeoFeatureClusteredPoint, buildGeometryToGeoFeatureLineOrArea, buildPointToGeoFeatureClusteredName, buildPointToGeoFeatureClusteredPoint } from "../utils/buildMethods";
import actionCreators from "./actionCreators";
import actionTypes from "./actionTypes";
import isString from "lodash/isString";
export const initialState = {
    [NAMESPACE]: {
        sourceData: {},
        geometry: {},
        points: {},
        nodeSources: {},
        clusteredNames: {},
        clusteredPoints: {},
        linesOrAreas: {},
        detailedPanelOpen: undefined,
        deleted: undefined,
        timelineData: {},
        mapOptions: {}
    }
};
const setMapDataForDocument = (state, payload, documentId) => {
    const timestampAttrKeys = ["PT10041", "PT10043", "PT11363", "PT10026"];
    forEach(payload.Points, p => {
        var _a, _b, _c;
        if (p.NodeSource) {
            const timestampAttr = (_c = (_b = (_a = p.NodeSource) === null || _a === void 0 ? void 0 : _a.AttributeValues) === null || _b === void 0 ? void 0 : _b.find(item => timestampAttrKeys.includes(item.Key))) === null || _c === void 0 ? void 0 : _c.Value;
            const timestamp = timestampAttr
                ? isString(timestampAttr)
                    ? parseInt(timestampAttr)
                    : timestampAttr
                : p.NodeSource.Created;
            p.NodeSource.Created = timestamp;
        }
    });
    let result = set(state, `[${NAMESPACE}].sourceData[${documentId}]`, cloneDeep(payload), {
        checkExistence: false,
        checkEqual: false
    });
    result = set(result, `[${NAMESPACE}].geometry[${documentId}]`, keyBy(payload.Geometry, "Id"), {
        checkExistence: false,
        checkEqual: false
    });
    result = set(result, `[${NAMESPACE}].nodeSources[${documentId}]`, keyBy(map(payload.Points, "NodeSource"), "NodeId"), {
        checkExistence: false,
        checkEqual: false
    });
    result = set(result, `[${NAMESPACE}].points[${documentId}]`, keyBy(map(payload.Points, p => omit(p, "NodeSource")), "Id"), {
        checkExistence: false,
        checkEqual: false
    });
    const timelineDataMap = {};
    const clusteredPoints = {};
    const clusteredNames = {};
    forEach(payload.Points, p => {
        var _a;
        const clusteredPoint = buildPointToGeoFeatureClusteredPoint(p);
        if (clusteredPoint)
            clusteredPoints[clusteredPoint.id] = clusteredPoint;
        const clusteredName = buildPointToGeoFeatureClusteredName(p);
        if (clusteredName)
            clusteredNames[clusteredName.id] = clusteredName;
        if ((_a = p.NodeSource) === null || _a === void 0 ? void 0 : _a.Created) {
            if (!timelineDataMap[p.NodeSource.Created])
                timelineDataMap[p.NodeSource.Created] = 0;
            timelineDataMap[p.NodeSource.Created] += 1;
        }
    });
    forEach(payload.Geometry, p => {
        const clusteredPoint = buildGeometryToGeoFeatureClusteredPoint(p);
        if (clusteredPoint)
            clusteredPoints[clusteredPoint.id] = clusteredPoint;
        const clusteredName = buildGeometryToGeoFeatureClusteredName(p);
        if (clusteredName)
            clusteredNames[clusteredName.id] = clusteredName;
    });
    result = set(result, `[${NAMESPACE}].clusteredPoints[${documentId}]`, clusteredPoints, {
        checkExistence: false,
        checkEqual: false
    });
    result = set(result, `[${NAMESPACE}].clusteredNames[${documentId}]`, clusteredNames, {
        checkExistence: false,
        checkEqual: false
    });
    result = set(result, `[${NAMESPACE}].timelineData[${documentId}]`, map(timelineDataMap, (count, created) => ({ Timestamp: Number.parseInt(created), Count: count })));
    result = set(result, "deleted", undefined);
    const linesOrAreas = compact(map(payload.Geometry, buildGeometryToGeoFeatureLineOrArea));
    return set(result, `[${NAMESPACE}].linesOrAreas[${documentId}]`, keyBy(linesOrAreas, "id"), {
        checkExistence: false,
        checkEqual: false
    });
};
export default createReducer(initialState, {
    [actionTypes.SET_MAP_DATA_FOR_DOCUMENT]: (state, { meta }) => setMapDataForDocument(state, meta.sourceData, meta.documentId),
    [actionTypes.ADD_GEOMETRY]: (state, { documentId, data }) => {
        if (!data.Id)
            return state;
        let result = set(state, `[${NAMESPACE}].geometry[${documentId}]`, {
            ...get(state, `[${NAMESPACE}].geometry[${documentId}]`, {}),
            [data.Id]: data
        }, { checkExistence: false, checkEqual: false });
        const clusteredName = buildGeometryToGeoFeatureClusteredName(data);
        if (clusteredName) {
            result = set(result, `[${NAMESPACE}].clusteredNames[${documentId}]`, {
                ...get(state, `[${NAMESPACE}].clusteredNames[${documentId}]`, {}),
                [clusteredName.id]: clusteredName
            }, { checkExistence: false, checkEqual: false });
        }
        const clusteredPoint = buildGeometryToGeoFeatureClusteredPoint(data);
        if (clusteredPoint) {
            result = set(result, `[${NAMESPACE}].clusteredPoints[${documentId}]`, {
                ...get(state, `[${NAMESPACE}].clusteredPoints[${documentId}]`, {}),
                [clusteredPoint.id]: clusteredPoint
            }, { checkExistence: false, checkEqual: false });
        }
        const lineOrArea = buildGeometryToGeoFeatureLineOrArea(data);
        if (lineOrArea) {
            result = set(result, `[${NAMESPACE}].linesOrAreas[${documentId}]`, {
                ...get(state, `[${NAMESPACE}].linesOrAreas[${documentId}]`, {}),
                [lineOrArea.id]: lineOrArea
            }, { checkExistence: false, checkEqual: false });
        }
        return data.Id
            ? set(result, `[${NAMESPACE}].selected`, [data.Id], {
                checkExistence: false,
                checkEqual: false
            })
            : result;
    },
    [actionTypes.UPDATE_MAP_DATA]: (state, { documentId, data, nameUpdated, colorUpdated, pointsCoordsUpdated }) => {
        let result = state;
        forEach(data, e => {
            const id = getId(e);
            if (!id)
                return;
            const isPoint = isDocumentMapDataPoint(e);
            const isGeometry = isDocumentMapDataGeometry(e);
            if (isPoint) {
                const nodeSource = e.NodeSource;
                if (nodeSource) {
                    result = set(result, `[${NAMESPACE}].nodeSources[${documentId}][${nodeSource.NodeId}]`, nodeSource, { checkExistence: false, checkEqual: false });
                }
            }
            else if (isGeometry) {
                result = set(result, `[${NAMESPACE}].geometry[${documentId}]`, {
                    ...get(result, `[${NAMESPACE}].geometry[${documentId}]`, {}),
                    [id]: e
                }, { checkExistence: false, checkEqual: false });
            }
            if ((nameUpdated || pointsCoordsUpdated) && isGeometry) {
                const clusteredName = buildGeometryToGeoFeatureClusteredName(e);
                if (clusteredName) {
                    result = set(result, `[${NAMESPACE}].clusteredNames[${documentId}]`, {
                        ...get(result, `[${NAMESPACE}].clusteredNames[${documentId}]`, {}),
                        [id]: clusteredName
                    }, { checkExistence: false, checkEqual: false });
                }
            }
            if (colorUpdated || pointsCoordsUpdated) {
                let clusteredPoint;
                if (isPoint) {
                    clusteredPoint = buildPointToGeoFeatureClusteredPoint(e);
                }
                else if (isGeometry) {
                    clusteredPoint = buildGeometryToGeoFeatureClusteredPoint(e);
                }
                if (clusteredPoint) {
                    result = set(result, `[${NAMESPACE}].clusteredPoints[${documentId}]`, {
                        ...get(result, `[${NAMESPACE}].clusteredPoints[${documentId}]`, {}),
                        [id]: clusteredPoint
                    }, { checkExistence: false, checkEqual: false });
                }
            }
            if ((nameUpdated || colorUpdated) && isGeometry) {
                const lineOrArea = buildGeometryToGeoFeatureLineOrArea(e);
                if (lineOrArea) {
                    result = set(result, `[${NAMESPACE}].linesOrAreas[${documentId}]`, {
                        ...get(result, `[${NAMESPACE}].linesOrAreas[${documentId}]`, {}),
                        [id]: lineOrArea
                    }, { checkExistence: false, checkEqual: false });
                }
            }
        });
        return result;
    },
    [actionTypes.SET_DETAILED_PANEL_OPEN]: (state, { detailedPanelOpen }) => set(state, `[${NAMESPACE}].detailedPanelOpen`, detailedPanelOpen),
    [actionTypes.CLEAR_DATA]: (state, { documentId }) => ({
        ...state,
        [NAMESPACE]: {
            ...state[NAMESPACE],
            sourceData: omit(state[NAMESPACE].sourceData, documentId),
            geometry: omit(state[NAMESPACE].geometry, documentId),
            points: omit(state[NAMESPACE].points, documentId),
            clusteredNames: omit(state[NAMESPACE].clusteredNames, documentId),
            clusteredPoints: omit(state[NAMESPACE].clusteredPoints, documentId),
            linesOrAreas: omit(state[NAMESPACE].linesOrAreas, documentId),
            timelineData: omit(state[NAMESPACE].timelineData, documentId),
            mapOptions: omit(state[NAMESPACE].mapOptions, documentId)
        }
    }),
    [actionTypes.SET_DELETED]: (state, { ids }) => set(state, `[${NAMESPACE}].deleted`, ids),
    [actionTypes.SET_MAP_OPTIONS]: (state, { documentId, center, zoom }) => set(state, `[${NAMESPACE}].mapOptions[${documentId}]`, { center, zoom })
});
listenerMiddleware.addListener({
    [actionTypes.GET_DOCUMENT_MAP_DATA_COMPLETED]: (action, dispatch, getState) => {
        const selectedDocumentIds = getSelectedDocumentIds(getState());
        if (action.meta.documentId && includes(selectedDocumentIds, action.meta.documentId)) {
            dispatch({
                type: actionTypes.SET_MAP_DATA_FOR_DOCUMENT,
                meta: { documentId: action.meta.documentId, sourceData: action.payload }
            });
        }
    },
    [actionTypes.SAVE_DOCUMENT_MAP_DATA_COMPLETED]: (action, dispatch, getState) => {
        const selectedDocumentIds = getSelectedDocumentIds(getState());
        if (action.meta.documentId && includes(selectedDocumentIds, action.meta.documentId)) {
            dispatch({ type: actionTypes.SET_MAP_DATA_FOR_DOCUMENT, meta: action.meta });
        }
    },
    [documentsActionTypes.DELETE_DOCUMENT_COMPLETED]: ({ meta }, dispatch) => {
        dispatch(actionCreators.clearData(meta.documentId));
    },
    [documentsSidebarMenuActionTypes.SET_DOCUMENT_SELECTED]: ({ documentId, selected }, dispatch) => {
        if (documentId && !selected)
            dispatch(actionCreators.clearData(documentId));
    }
});
